import React, { useState, useCallback, useRef } from 'react';
import { Select, Input, Upload, Empty, Spin, Button } from 'antd';
import { PlusOutlined, SearchOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import '../../../../css/global.css';
import Utils from "../../../../js/models/helper/Utils";

const styles = {
    mediaCard: {
        cursor: 'pointer',
        transition: 'transform 0.2s',
        '&:hover': {
            transform: 'scale(1.02)',
        },
    },
    mediaCardSelected: {
        outline: '2px solid var(--color-primary)',
    },
    mediaContent: {
        objectFit: 'cover' as const,
    },
    uploadText: {
        color: 'var(--color-text-secondary)',
        display: 'none', // Hidden by default for drag and drop
    },
    uploadTextVisible: {
        display: 'block',
    },
    fullWidthUpload: {
        '& .ant-upload-wrapper': {
            width: '100%',
        },
        '& .ant-upload': {
            width: '100%',
        }
    }
};

export interface MediaCategory {
    value: string;
    label: string;
}

export interface MediaItem {
    id: string;
    url: string;
    type: 'image' | 'video';
    thumbnail?: string;
    title: string;
    category: string;
    assetId?: string;
}

interface MediaPickerProps {
    categories: MediaCategory[];
    mediaItems: MediaItem[];
    selectedMedia?: MediaItem;
    onSelect: (media: MediaItem) => void;
    onUpload?: (file: File) => Promise<MediaItem>;
    allowUpload?: boolean;
    allowDragDrop?: boolean;
    type?: 'image' | 'video' | 'all';
    loading?: boolean;
    compact?: boolean;
    onBack?: () => void;
    showTitle?: boolean;
    mediaPerRow?: number;
    showCategories?: boolean;
    showSearch?: boolean;
    error?: string;
}

export const MediaPicker: React.FC<MediaPickerProps> = ({
    categories,
    mediaItems,
    selectedMedia,
    onSelect,
    onUpload,
    allowUpload = true,
    allowDragDrop = false,
    type = 'all',
    loading = false,
    compact = false,
    onBack,
    showTitle = false,
    mediaPerRow = 3,
    showCategories = true,
    showSearch = true,
    error,
}) => {
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [uploading, setUploading] = useState(false);
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);

    const filteredMedia = mediaItems.filter(item => {
        const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
        const matchesType = type === 'all' || item.type === type;
        const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesType && matchesSearch;
    });

    const handleSearch = useCallback((value: string) => {
        if (searchTimeout.current) {
            clearTimeout(searchTimeout.current);
        }
        searchTimeout.current = setTimeout(() => {
            setSearchQuery(value);
        }, 300);
    }, []);

    const handleUpload = async (file: UploadFile) => {
        console.log('MediaPicker handleUpload called', file);
        if (!onUpload || !file.originFileObj) return false;
        try {
            setUploading(true);
            const newMedia = await onUpload(file.originFileObj);
            console.log('MediaPicker upload result:', newMedia);
            onSelect(newMedia);
            return false;
        } catch (error) {
            console.error('MediaPicker upload failed:', error);
            return false;
        } finally {
            setUploading(false);
        }
    };

    const getGridClass = (cols: number) => {
        switch (cols) {
            case 1: return 'grid-cols-1';
            case 2: return 'grid-cols-2';
            case 3: return 'grid-cols-3';
            case 4: return 'grid-cols-4';
            case 5: return 'grid-cols-5';
            case 6: return 'grid-cols-6';
            default: return 'grid-cols-3';
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className={`flex items-center gap-3 ${!compact && 'mb-2'}`}>
                {compact && onBack ? (
                    <button 
                        className="border-0 p-0 bg-transparent cursor-pointer flex items-center" 
                        onClick={onBack}
                    >
                        <ArrowLeftOutlined />
                    </button>
                ) : null}
                {showTitle && (
                    <span className="m-0 ">Select {type === 'all' ? 'media' : type}</span>
                )}
                {!compact && onBack ? (
                    <Button onClick={onBack}>Cancel</Button>
                ) : null}
            </div>
            
            <div className="flex flex-col gap-3">
                {showCategories && (
                    <Select
                        className="w-full"
                        value={selectedCategory}
                        onChange={setSelectedCategory}
                        options={[
                            { value: 'all', label: 'All Categories' },
                            ...categories
                        ]}
                        size="middle"
                    />
                )}
                {showSearch && (
                    <Input
                        className="w-full"
                        placeholder="Search..."
                        prefix={<SearchOutlined />}
                        onChange={e => handleSearch(e.target.value)}
                    />
                )}
            </div>
            
            {allowUpload && (
                <div className="flex flex-col items-center gap-2 w-full">
                    <Upload
                        accept={type === 'image' ? 'image/*' : type === 'video' ? 'video/*' : 'image/*,video/*'}
                        showUploadList={false}
                        customRequest={async (options) => {
                            const { file, onSuccess, onError } = options;
                            console.log('MediaPicker customRequest called', file);
                            try {
                                setUploading(true);
                                if (onUpload) {
                                    const newMedia = await onUpload(file as File);
                                    console.log('MediaPicker upload result:', newMedia);
                                    onSelect(newMedia);
                                }
                                if (onSuccess) onSuccess({}, file);
                            } catch (error) {
                                console.error('MediaPicker upload failed:', error);
                                if (onError) onError(error as any);
                            } finally {
                                setUploading(false);
                            }
                        }}
                        className='w-full full-width-upload'
                        disabled={uploading}
                        {...(allowDragDrop ? { 
                            multiple: false,
                            listType: "picture-card",
                        } : {})}
                    >
                        {uploading ? (
                            <Spin />
                        ) : allowDragDrop ? (
                            <div className="w-full">
                                <PlusOutlined />
                                <div style={styles.uploadText}>Upload</div>
                            </div>
                        ) : (
                            <Button 
                                className='w-full'
                                icon={<PlusOutlined />}
                                type="default"
                            >
                                Upload {type === 'all' ? 'media' : type}
                            </Button>
                        )}
                    </Upload>
                    {allowDragDrop && (
                        <p style={{
                            ...styles.uploadText,
                            ...styles.uploadTextVisible,
                        }}>
                            Drag and drop or click to upload
                        </p>
                    )}
                </div>
            )}
            
            <div className={`grid ${getGridClass(mediaPerRow)} gap-4 overflow-y-auto`}>
                {loading ? (
                    <div className="col-span-full flex justify-center items-center h-[200px]">
                        <Spin size="large" />
                    </div>
                ) : filteredMedia.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <span style={{ color: error ? 'red' : 'inherit' }}>
                                {error ? error : 'No media found'}
                            </span>
                        }
                        style={{ margin: '0 auto', padding: '40px 0' }}
                    />
                ) : (
                    filteredMedia.map(item => (
                        <div
                            key={item.id}
                            className={
                                `overflow-hidden cursor-pointer box-border ` +
                                (item.category === 'background' || (item.type === 'image' && item.title?.toLowerCase().includes('background'))
                                    ? 'aspect-video'
                                    : 'aspect-square') +
                                (selectedMedia?.id === item.id ? ' ring-2 ring-primary' : '')
                            }
                            onClick={() => onSelect(item)}
                        >
                            <div
                                className="border-2 border-gray-200 bg-white flex items-center justify-center w-full h-full p-2 box-border dark:bg-neutral-800 dark:border-neutral-600"
                            >
                                {item.type === 'video' ? (
                                    <video
                                        src={Utils.getInstance().getCloudFrontUrlIfApplicable(item.url)}
                                        poster={item.thumbnail ? Utils.getInstance().getCloudFrontUrlIfApplicable(item.thumbnail) : undefined}
                                        preload="metadata"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <img
                                        alt={item.title}
                                        src={Utils.getInstance().getImageUrlResizeToSmallSize(item.url)}
                                        loading="lazy"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                            display: 'block',
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}; 